import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Brain, Sparkles, Target, Activity, MessageSquareWarning, Loader2, Zap } from "lucide-react";

function ScoreRing({ value, label, testid }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const color =
    v >= 80 ? "#10B981" :
    v >= 60 ? "#0055FF" :
    v >= 40 ? "#F59E0B" :
    "#E11D48";
  const dash = (v / 100) * 100;
  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <div className="relative w-20 h-20">
        <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none" stroke="#e4e4e7" strokeWidth="2.8"
          />
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none" stroke={color} strokeWidth="2.8"
            strokeDasharray={`${dash}, 100`} strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-mono font-black text-xl">
          {v}
        </div>
      </div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-2 text-center">
        {label}
      </div>
    </div>
  );
}

export function CoachSummaryCard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/agents/me/coach/summary");
      setSummary(data);
    } catch (err) {
      console.error("Coach summary load failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="border border-zinc-200 bg-white p-8" data-testid="coach-summary-loading">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
      </div>
    );
  }
  if (!summary) return null;

  return (
    <div className="border border-zinc-200 bg-white" data-testid="coach-summary-card">
      <div className="flex items-center justify-between p-6 border-b border-zinc-200">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-[#0055FF]" />
          <div>
            <div className="font-heading font-black tracking-tight text-xl">AI Coach</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              Powered by LeadBridge AI · {summary.analyzed_count} call{summary.analyzed_count === 1 ? "" : "s"} analyzed
            </div>
          </div>
        </div>
        <Button variant="ghost" size="sm" className="btn-sharp text-xs uppercase tracking-widest" onClick={load} data-testid="coach-summary-refresh">
          Refresh
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-200 border-b border-zinc-200">
        <div className="bg-white p-5" data-testid="coach-kpi-close-rate">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Close rate</div>
          <div className="font-mono font-black text-3xl tracking-tight mt-1">{summary.close_rate_pct}%</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
            {summary.billable_calls}/{summary.total_calls} billable
          </div>
        </div>
        <div className="bg-white p-5" data-testid="coach-kpi-transfers">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Transfers</div>
          <div className="font-mono font-black text-3xl tracking-tight mt-1">{summary.transfers}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
            {summary.transfer_rate_pct}% of calls
          </div>
        </div>
        <div className="bg-white p-5" data-testid="coach-kpi-avg-duration">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Avg duration</div>
          <div className="font-mono font-black text-3xl tracking-tight mt-1">{Math.round(summary.avg_duration_sec)}s</div>
        </div>
        <div className="bg-white p-5" data-testid="coach-kpi-total-calls">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Total calls</div>
          <div className="font-mono font-black text-3xl tracking-tight mt-1">{summary.total_calls}</div>
        </div>
      </div>

      {/* Score rings */}
      {summary.analyzed_count > 0 ? (
        <div className="p-6 border-b border-zinc-200">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 mb-4">
            Average performance scores
          </div>
          <div className="grid grid-cols-4 gap-4">
            <ScoreRing value={summary.avg_closing_score} label="Closing" testid="coach-score-closing" />
            <ScoreRing value={summary.avg_rapport_score} label="Rapport" testid="coach-score-rapport" />
            <ScoreRing value={summary.avg_tonality_score} label="Tonality" testid="coach-score-tonality" />
            <ScoreRing value={summary.avg_discovery_score} label="Discovery" testid="coach-score-discovery" />
          </div>
        </div>
      ) : (
        <div className="p-6 border-b border-zinc-200 text-center">
          <Sparkles className="w-6 h-6 mx-auto text-zinc-400" />
          <div className="text-sm text-zinc-600 mt-2">
            No calls analyzed yet. The AI Coach scores each call automatically once recordings are processed.
          </div>
        </div>
      )}

      {/* Top missed objections */}
      {summary.top_missed_objections.length > 0 && (
        <div className="p-6 border-b border-zinc-200" data-testid="coach-top-missed">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquareWarning className="w-4 h-4 text-rose-600" />
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">
              Top objections you're missing
            </div>
          </div>
          <div className="space-y-1.5">
            {summary.top_missed_objections.map((o, i) => (
              <div key={`${o.objection}-${i}`} className="flex items-center justify-between text-sm">
                <span className="font-mono text-zinc-700">{o.objection}</span>
                <span className="font-mono text-xs uppercase tracking-widest text-rose-700">
                  {o.count}x missed
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tips */}
      {summary.tips.length > 0 && (
        <div className="p-6" data-testid="coach-tips">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-[#10B981]" />
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">
              Coaching recommendations
            </div>
          </div>
          <ul className="space-y-2">
            {summary.tips.slice(0, 6).map((tip, i) => (
              <li key={`tip-${i}-${tip.slice(0, 20)}`} className="flex gap-3 text-sm text-zinc-800">
                <span className="font-mono font-bold text-[#0055FF] flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


export function ObjectionHandler() {
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim()) {
      toast.error("Type or paste what the caller said first");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/coach/objection", { text: text.trim() });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI lookup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-zinc-200 bg-white" data-testid="objection-handler-card">
      <div className="flex items-center gap-2 p-6 border-b border-zinc-200">
        <Zap className="w-5 h-5 text-amber-500" />
        <div>
          <div className="font-heading font-black tracking-tight text-xl">Objection Handler</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            Paste what the caller said · get 3 ranked rebuttals instantly
          </div>
        </div>
      </div>
      <div className="p-6 space-y-3">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. 'I need to talk to my wife first' or 'I cant afford that right now'"
          className="btn-sharp font-mono text-sm min-h-[80px]"
          data-testid="objection-input"
        />
        <div className="flex items-center gap-3">
          <Button
            onClick={submit}
            disabled={busy}
            className="btn-sharp bg-black hover:bg-zinc-800 text-white text-xs uppercase tracking-widest font-bold"
            data-testid="objection-submit-button"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            {busy ? "Thinking…" : "Get Rebuttals"}
          </Button>
          {result && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setResult(null); setText(""); }}
              className="btn-sharp text-xs uppercase tracking-widest"
              data-testid="objection-reset-button"
            >
              Reset
            </Button>
          )}
        </div>

        {result && (
          <div className="space-y-3 pt-2" data-testid="objection-result">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              Detected: <span className="text-black font-bold">{result.objection_type}</span>
            </div>
            {(result.rebuttals || []).map((r, i) => (
              <div key={`${r.label}-${i}`} className="border border-zinc-200 p-4 bg-zinc-50" data-testid={`objection-rebuttal-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-heading font-bold tracking-tight text-base">
                    <span className="font-mono text-[#0055FF] mr-2">#{i + 1}</span>
                    {r.label}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="btn-sharp text-[10px] uppercase tracking-widest"
                    onClick={() => {
                      navigator.clipboard.writeText(r.script);
                      toast.success("Copied to clipboard");
                    }}
                    data-testid={`objection-copy-${i}`}
                  >
                    Copy
                  </Button>
                </div>
                <div className="text-sm text-zinc-900 italic mb-2">"{r.script}"</div>
                <div className="text-[11px] font-mono text-zinc-500 uppercase tracking-widest">
                  Why · {r.why_it_works}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


export function CallCoachAnalysis({ leadId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/coach/lead/${leadId}`);
      setData(data.analysis);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const reanalyze = async () => {
    setRetrying(true);
    try {
      await api.post(`/coach/lead/${leadId}/reanalyze`);
      toast.success("Re-analysis queued. Refresh in a moment.");
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRetrying(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-400 inline" /></div>;
  }

  if (!data) {
    return (
      <div className="p-6 text-center" data-testid="coach-analysis-empty">
        <Activity className="w-6 h-6 mx-auto text-zinc-400" />
        <div className="text-sm text-zinc-600 mt-2">No AI analysis yet for this call.</div>
        <Button size="sm" onClick={reanalyze} disabled={retrying} className="btn-sharp mt-3 bg-black text-white text-xs uppercase tracking-widest" data-testid="coach-analyze-button">
          {retrying ? "Queuing…" : "Run Analysis"}
        </Button>
      </div>
    );
  }

  if (data.status === "running") {
    return <div className="p-6 text-center text-sm text-zinc-600"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />AI Coach is listening to the recording…</div>;
  }

  if (data.status === "failed") {
    return (
      <div className="p-6 text-center" data-testid="coach-analysis-failed">
        <div className="text-sm text-rose-700">Analysis failed: {data.error}</div>
        <Button size="sm" onClick={reanalyze} disabled={retrying} className="btn-sharp mt-3 bg-black text-white text-xs uppercase tracking-widest" data-testid="coach-retry-button">
          Retry
        </Button>
      </div>
    );
  }

  const a = data.analysis || {};
  return (
    <div className="p-6 space-y-5" data-testid="coach-analysis-done">
      {/* Scores */}
      <div className="grid grid-cols-4 gap-4 pb-5 border-b border-zinc-200">
        <ScoreRing value={a.closing_score} label="Closing" testid="lead-score-closing" />
        <ScoreRing value={a.rapport_score} label="Rapport" testid="lead-score-rapport" />
        <ScoreRing value={a.tonality_score} label="Tonality" testid="lead-score-tonality" />
        <ScoreRing value={a.discovery_score} label="Discovery" testid="lead-score-discovery" />
      </div>

      {/* Summary */}
      {a.summary && (
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 mb-2">Summary</div>
          <div className="text-sm text-zinc-900">{a.summary}</div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-5">
        {a.biggest_strength && (
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-emerald-700 mb-2">Strength</div>
            <div className="text-sm">{a.biggest_strength}</div>
          </div>
        )}
        {a.biggest_weakness && (
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-rose-700 mb-2">Weakness</div>
            <div className="text-sm">{a.biggest_weakness}</div>
          </div>
        )}
      </div>

      {(a.objections_raised || []).length > 0 && (
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 mb-2">
              Objections raised ({(a.objections_raised || []).length})
            </div>
            <ul className="space-y-1 text-sm">
              {(a.objections_raised || []).map((o, i) => (
                <li key={`raised-${i}-${o?.slice(0, 24)}`} className="text-zinc-700">· {o}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-rose-700 mb-2">
              Missed ({(a.objections_missed || []).length})
            </div>
            <ul className="space-y-1 text-sm">
              {(a.objections_missed || []).length === 0 ? (
                <li className="text-emerald-700 font-mono uppercase tracking-widest text-xs">None — clean handling</li>
              ) : (
                (a.objections_missed || []).map((o, i) => (
                  <li key={`missed-${i}-${o?.slice(0, 24)}`} className="text-rose-800">· {o}</li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}

      {(a.ideal_responses || []).length > 0 && (
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 mb-2">
            Ideal responses for missed objections
          </div>
          <div className="space-y-2">
            {(a.ideal_responses || []).map((r, i) => (
              <div key={`ideal-${i}-${(r.objection || "").slice(0, 24)}`} className="bg-zinc-50 border border-zinc-200 p-3" data-testid={`coach-ideal-${i}`}>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
                  Objection: {r.objection}
                </div>
                <div className="text-sm italic mt-1">"{r.ideal_response}"</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(a.tips || []).length > 0 && (
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 mb-2">Coaching tips</div>
          <ul className="space-y-2">
            {(a.tips || []).map((t, i) => (
              <li key={`atip-${i}-${(t || "").slice(0, 24)}`} className="flex gap-3 text-sm">
                <span className="font-mono font-bold text-[#0055FF]">{String(i + 1).padStart(2, "0")}</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-end">
        <Button size="sm" variant="ghost" onClick={reanalyze} disabled={retrying} className="btn-sharp text-xs uppercase tracking-widest" data-testid="coach-reanalyze-button">
          {retrying ? "Queuing…" : "Re-analyze"}
        </Button>
      </div>
    </div>
  );
}
